import React, { useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import MoneyInput from '../../shared/MoneyInput';
import NumberInput from '../../shared/NumberInput';
import PickSelect from '../../shared/PickSelect';
import { bakeoffYearOptions } from '../constants';
import { newProperty } from './useIncomeForms';
import { MissingMark, MissingPanel, SaveBar, VersionLine, Warnings, money } from './formParts';

// Form 92 (Freddie Mac Net Rental Income, Schedule E): one block per property (ticket 215).
const SIGN_BADGE = { add: '(+)', subtract: '(−)', signed: '(+/−)', reverse: '(+/−)' };
const CELL = 'w-full px-2 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-brand-navy';

function missingKeys(result) {
  const keys = new Set();
  (result?.missing_fields || []).forEach((m) => keys.add(`${m.property_index}|${m.line}|${m.year ?? '*'}`));
  return keys;
}

function PropertyCard({ idx, p, def, missing, onChange, onRemove }) {
  const yearOptions = useMemo(() => {
    const opts = bakeoffYearOptions();
    p.years.forEach((y) => {
      if (!opts.some((o) => o.value === String(y.tax_year))) opts.push({ value: String(y.tax_year), label: String(y.tax_year) });
    });
    return opts.sort((a, b) => Number(b.value) - Number(a.value));
  }, [p.years]);
  const cat = def.categories.find((c) => c.key === p.category);
  const shown = p.years.slice(0, p.yearCount);
  const hit = (line, year) => missing.has(`${idx}|${line}|${year}`) || missing.has(`${idx}|${line}|*`);
  const setYear = (i, fn) => onChange((pp) => ({ ...pp, years: pp.years.map((y, j) => (j === i ? fn(y) : y)) }));
  const cols = { gridTemplateColumns: `minmax(0,1fr) repeat(${shown.length}, 9rem)` };

  return (
    <div className="rounded-lg border border-slate-200 p-3 bg-slate-50 space-y-3" data-testid={`property-${idx}`}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <PickSelect
            label="Property type"
            value={p.category}
            onChange={(v) => onChange((pp) => ({ ...pp, category: v }))}
            options={def.categories.map((c) => ({ value: c.key, label: c.label }))}
            testId={`property-category-${idx}`}
          />
        </div>
        <div className="flex-1 min-w-48">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Address</div>
          <Input
            value={p.address}
            onChange={(e) => onChange((pp) => ({ ...pp, address: e.target.value }))}
            maxLength={120}
            className="mt-1 h-9 text-sm"
            data-testid={`property-address-${idx}`}
          />
        </div>
        {[1, 2].map((n) => (
          <button
            key={n}
            onClick={() => p.yearCount !== n && onChange((pp) => ({ ...pp, yearCount: n }))}
            className={`px-3 py-1 rounded-md text-xs font-semibold mb-1 ${p.yearCount === n ? 'bg-brand-navy text-white' : 'bg-slate-100 text-slate-600'}`}
            data-testid={`property-years-${idx}-${n}`}
          >{n} {n === 1 ? 'Year' : 'Years'}</button>
        ))}
        <button type="button" onClick={onRemove} className="p-1.5 hover:bg-red-50 rounded-lg mb-1" aria-label="Remove property" data-testid={`property-remove-${idx}`}>
          <X size={14} className="text-red-500" />
        </button>
      </div>

      <div>
        <div className="grid gap-2 mb-1" style={cols}>
          <div />
          {shown.map((y, i) => (
            <PickSelect
              key={i}
              value={String(y.tax_year)}
              onChange={(v) => setYear(i, (yy) => ({ ...yy, tax_year: Number(v) }))}
              options={yearOptions}
              testId={`property-year-${idx}-${i}`}
            />
          ))}
        </div>
        <div className="grid gap-2 items-center py-1.5 border-t border-slate-200" style={cols}>
          <div className="text-sm text-slate-800">Months <span className="text-amber-700" title="Required">*</span></div>
          {shown.map((y, i) => (
            <MissingMark key={i} missing={hit('months', y.tax_year)}>
              <NumberInput
                integer
                min={1}
                max={12}
                value={y.months ?? null}
                onChange={(n) => setYear(i, (yy) => ({ ...yy, months: n }))}
                aria-label={`Months ${y.tax_year}`}
                className={CELL}
                data-testid={`property-months-${idx}-${i}`}
              />
            </MissingMark>
          ))}
        </div>
        {def.lines.map((l) => (
          <div key={l.key} className="grid gap-2 items-start py-1.5 border-t border-slate-200" style={cols}>
            <div className="min-w-0">
              <div className="text-sm text-slate-800">
                {l.label}
                {l.required && <span className="text-amber-700 ml-1" title="Required">*</span>}
                <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">{SIGN_BADGE[l.kind] || ''}</span>
              </div>
              {l.source && <div className="text-[11px] text-slate-500">{l.source}</div>}
            </div>
            {shown.map((y, i) => (
              <MissingMark key={i} missing={hit(l.key, y.tax_year)}>
                <MoneyInput
                  value={y.lines[l.key] ?? null}
                  onChange={(n) => setYear(i, (yy) => ({ ...yy, lines: { ...yy.lines, [l.key]: n } }))}
                  placeholder=""
                  aria-label={`${l.label} ${y.tax_year}`}
                  className={CELL}
                  data-testid={`property-line-${idx}-${i}-${l.key}`}
                />
              </MissingMark>
            ))}
          </div>
        ))}
      </div>

      {cat?.needs_monthly_payment && (
        <div className="max-w-xs">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Monthly payment <span className="text-amber-700">*</span></div>
          <MissingMark missing={missing.has(`${idx}|monthly_payment|*`) || shown.some((y) => missing.has(`${idx}|monthly_payment|${y.tax_year}`))}>
            <MoneyInput
              value={p.monthly_payment ?? null}
              onChange={(n) => onChange((pp) => ({ ...pp, monthly_payment: n }))}
              placeholder=""
              aria-label="Monthly payment"
              className={`${CELL} mt-1`}
              data-testid={`property-payment-${idx}`}
            />
          </MissingMark>
        </div>
      )}
    </div>
  );
}

function ResultPanel({ def, result }) {
  const catLabel = (key) => def.categories.find((c) => c.key === key)?.label || key;
  const groups = [
    ['subject_primary', catLabel('subject_primary_2_4')],
    ['subject_investment', catLabel('subject_investment')],
    ['non_subject_combined', `${catLabel('non_subject_investment')} (combined)`],
  ];
  return (
    <div className="space-y-3" data-testid="income-form-result">
      {!result.complete && <MissingPanel result={result} />}
      {(result.properties || []).map((rp, i) => (
        <div key={i} className="rounded-lg border border-slate-200 overflow-hidden text-sm" data-testid={`income-result-property-${i}`}>
          <div className="px-3 py-2 bg-slate-50 font-semibold text-slate-800">{catLabel(rp.category)} — {rp.address}</div>
          <div className="px-3 py-2 space-y-1">
            {def.lines.map((l) => (
              <div key={l.key} className="flex justify-between gap-3 text-slate-600">
                <span>{l.label}</span>
                <span className="tabular-nums flex gap-6">
                  {(rp.years || []).map((y) => <span key={y.tax_year}>{y.tax_year}: {money(y.lines?.[l.key])}</span>)}
                </span>
              </div>
            ))}
            <div className="flex justify-between gap-3 font-semibold text-slate-800">
              <span>Yearly subtotal</span>
              <span className="tabular-nums flex gap-6">
                {(rp.years || []).map((y) => <span key={y.tax_year}>{y.tax_year}: {money(y.subtotal)}</span>)}
              </span>
            </div>
            <div className="flex justify-between text-slate-600"><span>Total subtotal / months</span><span className="tabular-nums">{money(rp.total_subtotal)} / {rp.total_months ?? '—'}</span></div>
            <div className="flex justify-between text-slate-800"><span>Net rental income (monthly)</span><span className="tabular-nums" data-testid={`income-property-net-${i}`}>{money(rp.net_rental_income)}</span></div>
            {rp.monthly_payment != null && <div className="flex justify-between text-slate-600"><span>Monthly payment</span><span className="tabular-nums">{money(rp.monthly_payment)}</span></div>}
            <div className="flex justify-between font-bold text-slate-900"><span>Result</span><span className="tabular-nums" data-testid={`income-property-result-${i}`}>{money(rp.result)}</span></div>
          </div>
        </div>
      ))}
      <div className="rounded-lg border border-slate-200 px-3 py-2 text-sm space-y-1" data-testid="income-form-groups">
        {groups.map(([key, label]) => (
          <div key={key} className="flex justify-between text-slate-700">
            <span>{label}</span>
            <span className="tabular-nums" data-testid={`income-group-${key}`}>{money(result[key])}</span>
          </div>
        ))}
      </div>
      {result.summary && (
        <div className="rounded-lg px-4 py-3 bg-emerald-50 border border-emerald-200" data-testid="income-form-summary">
          <div className="flex justify-between text-sm text-emerald-900"><span>Add to income</span><span className="font-bold text-lg text-emerald-700" data-testid="income-add-to-income">{money(result.summary.add_to_income)}</span></div>
          <div className="flex justify-between text-sm text-emerald-900"><span>Add to liabilities</span><span className="font-bold text-lg text-emerald-700" data-testid="income-add-to-liabilities">{money(result.summary.add_to_liabilities)}</span></div>
        </div>
      )}
      <Warnings warnings={result.warnings} />
    </div>
  );
}

export function RentalForm({ formId, def, ctl }) {
  const f = ctl.forms[formId];
  const d = f.draft;
  const missing = useMemo(() => missingKeys(f.result), [f.result]);
  const up = (fn) => ctl.update(formId, fn);
  const setProp = (idx, fn) => up((dr) => ({ ...dr, properties: dr.properties.map((p, i) => (i === idx ? fn(p) : p)) }));
  const canCalc = !f.busy && d.properties.length > 0;

  return (
    <div className="space-y-4" data-testid={`income-form-${formId}`}>
      <VersionLine def={def} />

      {d.properties.map((p, idx) => (
        <PropertyCard
          key={p.uid}
          idx={idx}
          p={p}
          def={def}
          missing={missing}
          onChange={(fn) => setProp(idx, fn)}
          onRemove={() => up((dr) => ({ ...dr, properties: dr.properties.filter((_, i) => i !== idx) }))}
        />
      ))}

      <Button
        variant="outline"
        onClick={() => up((dr) => ({ ...dr, properties: [...dr.properties, newProperty(def.categories[0].key)] }))}
        data-testid="income-add-property"
      >
        <Plus size={14} /> Add property
      </Button>

      <Button onClick={() => ctl.calculate(formId)} disabled={!canCalc} variant="navy" className="w-full" data-testid="income-calculate">
        {f.busy ? 'Working…' : 'Calculate'}
      </Button>
      {d.properties.length === 0 && (
        <div className="text-xs text-slate-500" data-testid="income-form-hint">Add one block per rental property on Schedule E.</div>
      )}

      {f.result && <ResultPanel def={def} result={f.result} />}

      <SaveBar formId={formId} ctl={ctl} />
    </div>
  );
}
